import { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './PinCodes.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const blankForm = () => ({
  pincode: '',
  default_assignee: '',
  distance: '',
  min_order_value: '',
  delivery_fee: '',
  column11: '',
});

const getAuthHeader = (token) => token ? { Authorization: `Bearer ${token}` } : {};

/* ── Modal ─────────────────────────────────────────────────── */
const PinCodeModal = ({ initial, usersLov, onSave, onClose }) => {
  const { user } = useContext(AuthContext);
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(
    initial
      ? {
          pincode: initial.pincode?.trim() || '',
          default_assignee: initial.default_assignee ?? '',
          distance: initial.distance ?? '',
          min_order_value: initial.min_order_value ?? '',
          delivery_fee: initial.delivery_fee ?? '',
          column11: initial.column11 || '',
        }
      : blankForm()
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.pincode.trim() || form.pincode.trim().length !== 6) {
      setError('Pincode must be exactly 6 digits.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const headers = getAuthHeader(user?.token);
      if (isEdit) {
        await axios.put(`${API_URL}/pincodes/${initial.id}`, form, { headers });
      } else {
        await axios.post(`${API_URL}/pincodes`, form, { headers });
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save pincode.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pc-modal-overlay" onClick={onClose}>
      <div className="pc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pc-modal-header">
          <h2>{isEdit ? `Edit — ${initial.pincode?.trim()}` : 'Add Pincode'}</h2>
          <button className="pc-modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="pc-modal-body">
          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠</span>{error}
            </div>
          )}

          <div className="pc-form-row">
            <div className="pc-form-group pc-form-group--full">
              <label>Pincode *</label>
              <input
                className="pc-input"
                value={form.pincode}
                maxLength={6}
                placeholder="6-digit pincode"
                onChange={(e) => set('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
              />
            </div>
          </div>

          <div className="pc-form-row">
            <div className="pc-form-group">
              <label>Default Assignee</label>
              <select
                className="pc-input"
                value={form.default_assignee}
                onChange={(e) => set('default_assignee', e.target.value)}
              >
                <option value="">— none —</option>
                {usersLov.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
            <div className="pc-form-group">
              <label>Distance (km)</label>
              <input
                className="pc-input"
                type="number"
                step="0.01"
                min="0"
                value={form.distance}
                onChange={(e) => set('distance', e.target.value)}
                placeholder="e.g. 5.50"
              />
            </div>
          </div>

          <div className="pc-form-row">
            <div className="pc-form-group">
              <label>Min Order Value (₹)</label>
              <input
                className="pc-input"
                type="number"
                step="0.01"
                min="0"
                value={form.min_order_value}
                onChange={(e) => set('min_order_value', e.target.value)}
                placeholder="e.g. 200.00"
              />
            </div>
            <div className="pc-form-group">
              <label>Delivery Fee (₹)</label>
              <input
                className="pc-input"
                type="number"
                step="0.01"
                min="0"
                value={form.delivery_fee}
                onChange={(e) => set('delivery_fee', e.target.value)}
                placeholder="e.g. 30.00"
              />
            </div>
          </div>

          <div className="pc-form-row">
            <div className="pc-form-group pc-form-group--full">
              <label>Notes</label>
              <input
                className="pc-input"
                value={form.column11}
                maxLength={50}
                onChange={(e) => set('column11', e.target.value)}
                placeholder="Optional notes"
              />
            </div>
          </div>

          <div className="pc-modal-footer">
            <button type="button" className="pc-btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="pc-btn-submit" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Update' : 'Add Pincode'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Main Page ─────────────────────────────────────────────── */
const PinCodes = () => {
  const { user } = useContext(AuthContext);
  const auth = useCallback(() => ({ headers: getAuthHeader(user?.token) }), [user]);

  const [rows, setRows] = useState([]);
  const [usersLov, setUsersLov] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async (pin = search) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/pincodes`, {
        params: pin ? { pincode: pin } : {},
        ...auth(),
      });
      setRows(res.data);
    } catch {
      setError('Failed to load pincodes.');
    } finally {
      setLoading(false);
    }
  }, [auth, search]);

  useEffect(() => {
    load('');
    axios.get(`${API_URL}/commonLOVs/lhusers`, auth())
      .then((r) => setUsersLov(r.data))
      .catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    load(search);
  };

  const handleClear = () => {
    setSearch('');
    load('');
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete pincode ${row.pincode.trim()}?`)) return;
    try {
      await axios.delete(`${API_URL}/pincodes/${row.id}`, auth());
      setSuccess(`Pincode ${row.pincode.trim()} deleted.`);
      setTimeout(() => setSuccess(''), 4000);
      load(search);
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed.');
    }
  };

  const fmt = (v) => (v != null && v !== '' ? Number(v).toFixed(2) : '—');

  return (
    <div className="users-management">
      <div className="users-header">
        <div className="header-content">
          <h1>Pin Codes</h1>
          <p>Manage delivery pincodes, fees, and assignees</p>
        </div>
        <button className="btn-add-user" onClick={() => setModal({ type: 'add' })}>
          <span className="icon">+</span> Add Pincode
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠</span>{error}
          <button className="alert-close" onClick={() => setError('')}>×</button>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✓</span>{success}
          <button className="alert-close" onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      {/* Search bar */}
      <form className="pc-search-bar" onSubmit={handleSearch}>
        <div className="pc-search-input-wrap">
          <span className="pc-search-icon">🔍</span>
          <input
            className="pc-search-input"
            type="text"
            placeholder="Search by pincode…"
            value={search}
            maxLength={6}
            onChange={(e) => setSearch(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />
          {search && (
            <button type="button" className="pc-search-clear" onClick={handleClear}>×</button>
          )}
        </div>
        <button type="submit" className="pc-search-btn">Search</button>
      </form>

      <div className="pc-count">{rows.length} record{rows.length !== 1 ? 's' : ''}</div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Pincode</th>
              <th>Default Assignee</th>
              <th style={{ textAlign: 'right' }}>Distance (km)</th>
              <th style={{ textAlign: 'right' }}>Min Order (₹)</th>
              <th style={{ textAlign: 'right' }}>Delivery Fee (₹)</th>
              <th>Notes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="no-data">Loading…</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="no-data">No pincodes found.</td>
              </tr>
            ) : rows.map((row) => (
              <tr key={row.id}>
                <td><strong className="pc-pin">{row.pincode?.trim()}</strong></td>
                <td>{row.assignee_name || <span style={{ color: '#bbb' }}>—</span>}</td>
                <td style={{ textAlign: 'right' }}>{fmt(row.distance)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(row.min_order_value)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(row.delivery_fee)}</td>
                <td>{row.column11 || <span style={{ color: '#bbb' }}>—</span>}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-edit"
                      title="Edit"
                      onClick={() => setModal({ type: 'edit', row })}
                    >✏️</button>
                    <button
                      className="btn-delete"
                      title="Delete"
                      onClick={() => handleDelete(row)}
                    >🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal?.type === 'add' && (
        <PinCodeModal
          usersLov={usersLov}
          onSave={() => {
            setModal(null);
            setSuccess('Pincode saved.');
            setTimeout(() => setSuccess(''), 4000);
            load(search);
          }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'edit' && (
        <PinCodeModal
          initial={modal.row}
          usersLov={usersLov}
          onSave={() => {
            setModal(null);
            setSuccess('Pincode updated.');
            setTimeout(() => setSuccess(''), 4000);
            load(search);
          }}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default PinCodes;
